// Support tool for Apple subscription links: search/inspect the link table,
// force a re-verify against Apple, unlink a stuck subscription, or transfer
// one to a different user. Not a BI dashboard — the KPIs exist to spot
// support fires (stale caches, expiring soon), not to chart growth.

import { el, clear, fmtInt, fmtDate, relativeTime, scrollBelowHeader } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { kpiCard, kpiGrid, caption } from '../ui/components';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import {
  getSubscriptionStats,
  getSubscriptionLinks,
  getSubscriptionLinkById,
  unlinkSubscription,
  transferSubscription,
  verifySubscription,
  getTransferLog,
  getSubscriptionProducts,
  getSubscriptionStatusTypes,
} from '../api/subscriptions';
import type {
  SubscriptionStats,
  SubscriptionLinkRow,
  SubscriptionLinkDetail,
  SubscriptionLinksParams,
  SubscriptionStatus,
  TransferLogRow,
  TransferLogParams,
  PageMeta,
} from '../api/subscriptions';

export async function renderSubscriptionsAdmin({ root }: RouteContext) {
  const content = renderShell(root, 'subscriptions');
  const body = el('div', { class: 'detail-body' });
  content.append(body);

  renderLoading(body, 'Loading subscriptions…');

  let stats: SubscriptionStats;
  let products: string[];
  let statusTypes: string[];
  try {
    [stats, products, statusTypes] = await Promise.all([
      getSubscriptionStats(),
      getSubscriptionProducts().catch(() => []),
      getSubscriptionStatusTypes().catch(() => []),
    ]);
  } catch (err) {
    if (err instanceof UnauthorizedError) return navigate('/login');
    renderError(body, err instanceof Error ? err.message : 'Failed to load subscriptions.');
    return;
  }

  clear(body);

  // ── Stats ────────────────────────────────────────────────────────────
  const statsHost = el('div', {});
  renderStatsCards(statsHost, stats);

  async function reloadStats() {
    try {
      const next = await getSubscriptionStats();
      renderStatsCards(statsHost, next);
    } catch {
      /* best-effort — KPIs just stay stale until the next full load */
    }
  }

  // ── Links list ───────────────────────────────────────────────────────
  const linksState: {
    page: number;
    search: string;
    status: SubscriptionStatus | '';
    productId: string;
    sortBy: NonNullable<SubscriptionLinksParams['sortBy']>;
    sortDirection: 'asc' | 'desc';
  } = {
    page: 1,
    search: '',
    status: '',
    productId: '',
    sortBy: 'linkedAt',
    sortDirection: 'desc',
  };

  const searchInput = el('input', {
    class: 'control control-search',
    type: 'search',
    placeholder: 'Search by email, transaction ID, or user ID…',
  });

  const statusOptions: [string, string][] = [
    ['', 'All statuses'],
    ...(statusTypes.length
      ? statusTypes
      : ['active', 'expired', 'in_trial', 'grace_period', 'revoked']
    ).map((s): [string, string] => [s, formatStatusLabel(s)]),
  ];
  const statusSelect = selectControl(statusOptions, '');

  const productOptions: [string, string][] = [
    ['', 'All products'],
    ...products.map((p): [string, string] => [p, formatProductId(p)]),
  ];
  const productSelect = selectControl(productOptions, '');

  const sortSelect = selectControl(
    [
      ['linkedAt:desc', 'Newest linked'],
      ['linkedAt:asc', 'Oldest linked'],
      ['userEmail:asc', 'Email A–Z'],
      ['productId:asc', 'Product'],
    ],
    'linkedAt:desc',
  );

  const resetBtn = el('button', { class: 'btn btn-ghost', type: 'button' }, ['Reset']);

  const linksControls = el('div', { class: 'controls' }, [
    searchInput,
    el('div', { class: 'controls-right' }, [statusSelect, productSelect, sortSelect, resetBtn]),
  ]);

  const linksResults = el('div', { class: 'results' });

  async function loadLinks() {
    renderLoading(linksResults, 'Loading subscription links…');
    try {
      const params: SubscriptionLinksParams = {
        page: linksState.page,
        sortBy: linksState.sortBy,
        sortDirection: linksState.sortDirection,
        search: linksState.search || undefined,
        status: linksState.status || undefined,
        productId: linksState.productId || undefined,
      };
      const data = await getSubscriptionLinks(params);
      clear(linksResults);
      linksResults.append(
        linksTable(data.data, (id) => void openDetail(id)),
        paginationRow(data.meta, 'links', (p) => {
          linksState.page = p;
          void loadLinks();
        }),
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(
        linksResults,
        err instanceof Error ? err.message : 'Failed to load subscription links.',
      );
    }
  }

  let searchDebounce: number | undefined;
  searchInput.addEventListener('input', () => {
    window.clearTimeout(searchDebounce);
    searchDebounce = window.setTimeout(() => {
      linksState.search = searchInput.value.trim();
      linksState.page = 1;
      void loadLinks();
    }, 350);
  });
  statusSelect.addEventListener('change', () => {
    linksState.status = statusSelect.value as SubscriptionStatus | '';
    linksState.page = 1;
    void loadLinks();
  });
  productSelect.addEventListener('change', () => {
    linksState.productId = productSelect.value;
    linksState.page = 1;
    void loadLinks();
  });
  sortSelect.addEventListener('change', () => {
    const [by, dir] = sortSelect.value.split(':');
    linksState.sortBy = by as NonNullable<SubscriptionLinksParams['sortBy']>;
    linksState.sortDirection = dir as 'asc' | 'desc';
    void loadLinks();
  });
  resetBtn.addEventListener('click', () => {
    searchInput.value = '';
    statusSelect.value = '';
    productSelect.value = '';
    sortSelect.value = 'linkedAt:desc';
    linksState.page = 1;
    linksState.search = '';
    linksState.status = '';
    linksState.productId = '';
    linksState.sortBy = 'linkedAt';
    linksState.sortDirection = 'desc';
    void loadLinks();
  });

  // ── Link detail ──────────────────────────────────────────────────────
  const detailHost = el('div', {});
  let currentDetailId: string | null = null;

  // A row click shows the loading box and scrolls to it. A refresh after Verify
  // or Transfer instead keeps the panel on screen, swaps in fresh data and
  // carries the success message onto it (the loading box would have erased it).
  // Only the newest request may paint: an older response arriving last, or one
  // arriving after Close, must not overwrite what the operator is looking at.
  let detailSeq = 0;

  async function openDetail(id: string, refresh?: { notice: string }) {
    currentDetailId = id;
    const seq = ++detailSeq;
    if (!refresh) {
      renderLoading(detailHost, 'Loading subscription…');
      scrollBelowHeader(detailHost);
    }
    try {
      const detail = await getSubscriptionLinkById(id);
      if (seq !== detailSeq) return;
      renderDetail(detailHost, detail, handleDetailChanged, closeDetail, refresh?.notice);
      // The first scroll ran while the panel was the small loading box, so the
      // browser clamped it to the page's old end; scroll again now it has height.
      if (!refresh) scrollBelowHeader(detailHost);
    } catch (err) {
      if (seq !== detailSeq) return;
      if (err instanceof UnauthorizedError) return navigate('/login');
      const message = err instanceof Error ? err.message : 'Failed to load subscription.';
      renderError(
        detailHost,
        refresh ? `${refresh.notice} — but the panel could not be refreshed: ${message}` : message,
      );
    }
  }

  function closeDetail() {
    currentDetailId = null;
    detailSeq++;
    clear(detailHost);
  }

  function handleDetailChanged(change: DetailChange) {
    // Verify, Unlink and Transfer answer after a wait, and the operator may have
    // opened another row meanwhile: that panel is not this change's to touch.
    const stillOpen = currentDetailId === change.linkId;
    if (change.kind === 'unlinked') {
      if (stillOpen) closeDetail();
      void loadLinks();
      void reloadStats();
      return;
    }
    if (stillOpen) void openDetail(change.linkId, { notice: change.notice });
    if (change.kind === 'transferred') {
      void loadLinks();
      void loadTransfers();
      void reloadStats();
    }
  }

  // ── Transfer audit log (secondary, collapsed by default) ────────────
  const transfersState: {
    page: number;
    search: string;
  } = { page: 1, search: '' };

  const transfersSearchInput = el('input', {
    class: 'control control-search',
    type: 'search',
    placeholder: 'Search transfers by email or user ID…',
  });
  const transfersResults = el('div', { class: 'results' });

  async function loadTransfers() {
    renderLoading(transfersResults, 'Loading transfer history…');
    try {
      const params: TransferLogParams = {
        page: transfersState.page,
        search: transfersState.search || undefined,
      };
      const data = await getTransferLog(params);
      clear(transfersResults);
      transfersResults.append(
        transferHistoryTable(data.data),
        paginationRow(data.meta, 'transfers', (p) => {
          transfersState.page = p;
          void loadTransfers();
        }),
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(
        transfersResults,
        err instanceof Error ? err.message : 'Failed to load transfer history.',
      );
    }
  }

  let transfersDebounce: number | undefined;
  transfersSearchInput.addEventListener('input', () => {
    window.clearTimeout(transfersDebounce);
    transfersDebounce = window.setTimeout(() => {
      transfersState.search = transfersSearchInput.value.trim();
      transfersState.page = 1;
      void loadTransfers();
    }, 350);
  });

  let transfersLoaded = false;
  const transfersDetails = el(
    'details',
    { class: 'detail-section' },
    [el('summary', { textContent: 'Transfer audit log' })],
  );
  transfersDetails.addEventListener('toggle', () => {
    if (transfersDetails.open && !transfersLoaded) {
      transfersLoaded = true;
      void loadTransfers();
    }
  });
  transfersDetails.append(
    el('div', { style: 'margin-top:12px' }, [transfersSearchInput]),
    transfersResults,
  );

  // ── Assemble ─────────────────────────────────────────────────────────
  body.append(
    el('div', { class: 'page-head' }, [el('h1', {}, ['Subscriptions'])]),
    caption('Apple subscription links — search, inspect, and fix support cases.'),
    statsHost,
    el('div', { class: 'section-head' }, [el('h2', {}, ['Links'])]),
    linksControls,
    linksResults,
    detailHost,
    transfersDetails,
  );

  await loadLinks();
}

// ── Stats ──────────────────────────────────────────────────────────────

function activeSubscriptionsCount(stats: SubscriptionStats): number {
  const activeStatuses = new Set(['active', 'in_trial', 'grace_period']);
  return stats.statusBreakdown
    .filter((s) => activeStatuses.has(s.status))
    .reduce((sum, s) => sum + s.count, 0);
}

function renderStatsCards(container: HTMLElement, stats: SubscriptionStats): void {
  clear(container);
  container.append(
    kpiGrid([
      kpiCard('Total links', fmtInt(stats.totalLinks), {
        sub: `${fmtInt(stats.linksLast7Days)} in last 7d`,
      }),
      kpiCard('Active', fmtInt(activeSubscriptionsCount(stats)), { tone: 'ok' }),
      kpiCard('Expiring soon', fmtInt(stats.expiringSoon), {
        tone: stats.expiringSoon > 0 ? 'warn' : 'default',
        sub: 'Next 7 days',
      }),
      kpiCard('Stale', fmtInt(stats.staleStatuses), {
        tone: stats.staleStatuses > 0 ? 'danger' : 'default',
        sub: 'Not verified 24h+',
      }),
      kpiCard('Transfers (30d)', fmtInt(stats.transfersLast30Days), {
        sub: `${fmtInt(stats.totalTransfers)} all-time`,
      }),
      kpiCard('Active trials', fmtInt(stats.activeTrials)),
    ]),
  );
}

// ── Links table ───────────────────────────────────────────────────────

function linksTable(
  rows: SubscriptionLinkRow[],
  onOpen: (id: string) => void,
): HTMLElement {
  if (rows.length === 0) {
    return el('div', { class: 'empty' }, ['No subscription links match.']);
  }
  const headers = ['User', 'Product', 'Status', 'Expires', 'Linked'];
  const thead = el('thead', {}, [
    el('tr', {}, headers.map((h) => el('th', { textContent: h }))),
  ]);
  const rowEls = rows.map((r) =>
    el(
      'tr',
      { class: 'row-clickable', onClick: () => onOpen(r.id) },
      [
        el('td', {}, [
          el('div', { class: 'cell-strong', textContent: r.userEmail || r.userId }),
          r.userName ? el('div', { class: 'cell-muted', textContent: r.userName }) : null,
        ]),
        el('td', { class: 'cell-muted', textContent: formatProductId(r.productId) }),
        el('td', {}, [
          el('span', {
            class: statusPillClass(r.status),
            textContent: formatStatusLabel(r.status),
          }),
        ]),
        el('td', { class: 'cell-muted', textContent: fmtDate(r.expiresAt) }),
        el('td', { class: 'cell-muted', textContent: fmtDate(r.linkedAt) }),
      ],
    ),
  );
  return el('div', { class: 'table-wrap' }, [
    el('table', { class: 'data-table' }, [thead, el('tbody', {}, rowEls)]),
  ]);
}

// ── Link detail panel ────────────────────────────────────────────────

/** What a detail panel reports after an action changed the link, with the message to keep on screen. */
type DetailChange =
  | { kind: 'verified' | 'transferred'; linkId: string; notice: string }
  | { kind: 'unlinked'; linkId: string };

function renderDetail(
  container: HTMLElement,
  detail: SubscriptionLinkDetail,
  onChanged: (change: DetailChange) => void,
  onClose: () => void,
  notice?: string,
): void {
  clear(container);

  const statusHost = el('div', {});
  const setMessage = (msg: string, tone: 'ok' | 'error') => {
    clear(statusHost);
    statusHost.append(
      el(
        'div',
        {
          class: tone === 'error' ? 'banner banner-error' : 'banner',
          style: tone === 'ok' ? 'background:var(--ok-bg);color:var(--ok-fg)' : '',
          role: 'alert',
        },
        [msg],
      ),
    );
  };
  const clearMessage = () => clear(statusHost);
  // The message of the action that led to this refresh, so it is still on screen.
  if (notice) setMessage(notice, 'ok');

  const stale = detail.status ? isStale(detail.status.lastVerifiedAt) : false;

  const head = el('div', { class: 'detail-head' }, [
    el('div', {}, [
      el('h2', { textContent: detail.user.email || detail.user.name || detail.user.id }),
      el('div', { class: 'cell-muted', textContent: detail.originalTransactionId }),
    ]),
    el('div', { class: 'detail-head-pills' }, [
      el('span', {
        class: statusPillClass(detail.status?.status ?? null),
        textContent: formatStatusLabel(detail.status?.status ?? null),
      }),
      stale ? el('span', { class: 'pill pill-warn', textContent: 'Stale' }) : null,
    ]),
  ]);

  const meta = el('div', { class: 'meta-row' }, [
    metaItem('Product', formatProductId(detail.productId)),
    metaItem('Linked', fmtDate(detail.linkedAt)),
    metaItem('Expires', detail.status ? fmtDate(detail.status.expiresAt) : '—'),
    metaItem('Auto-renew', detail.status ? (detail.status.willAutoRenew ? 'Yes' : 'No') : '—'),
    metaItem('In trial', detail.status ? (detail.status.isInTrial ? 'Yes' : 'No') : '—'),
    metaItem(
      'Last verified',
      detail.status ? relativeTime(detail.status.lastVerifiedAt) : 'never',
    ),
    detail.transferredFromUserId
      ? metaItem('Transferred', fmtDate(detail.transferredAt))
      : null,
  ]);

  // Actions
  const verifyBtn = el('button', { class: 'btn btn-ghost', type: 'button' }, [
    'Force re-verify with Apple',
  ]);
  const transferBtn = el('button', { class: 'btn btn-ghost', type: 'button' }, [
    'Transfer to another user',
  ]);
  const unlinkBtn = el('button', { class: 'btn btn-ghost btn-danger', type: 'button' }, [
    'Unlink',
  ]);
  const closeBtn = el('button', { class: 'btn btn-ghost', type: 'button' }, ['Close']);
  closeBtn.addEventListener('click', onClose);

  verifyBtn.addEventListener('click', async () => {
    if (
      !window.confirm(
        'Force re-verify this subscription with Apple? This overwrites the cached status.',
      )
    ) {
      return;
    }
    clearMessage();
    verifyBtn.disabled = true;
    verifyBtn.textContent = 'Verifying…';
    try {
      const result = await verifySubscription(detail.originalTransactionId);
      setMessage(result.message, result.success ? 'ok' : 'error');
      if (result.success) {
        onChanged({ kind: 'verified', linkId: detail.id, notice: result.message });
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Verification failed.', 'error');
    } finally {
      verifyBtn.disabled = false;
      verifyBtn.textContent = 'Force re-verify with Apple';
    }
  });

  unlinkBtn.addEventListener('click', async () => {
    if (
      !window.confirm(
        `Unlink this subscription from ${detail.user.email ?? detail.user.id}?`,
      )
    ) {
      return;
    }
    clearMessage();
    unlinkBtn.disabled = true;
    try {
      const result = await unlinkSubscription(detail.id);
      if (result.success) {
        onChanged({ kind: 'unlinked', linkId: detail.id });
      } else {
        setMessage(result.message, 'error');
        unlinkBtn.disabled = false;
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Unlink failed.', 'error');
      unlinkBtn.disabled = false;
    }
  });

  // Transfer inline form (toggled by the button above).
  const toUserIdInput = el('input', {
    class: 'control',
    type: 'text',
    placeholder: 'Target user ID',
  });
  const reasonInput = el('input', {
    class: 'control',
    type: 'text',
    placeholder: 'Reason (optional)',
  });
  const confirmTransferBtn = el(
    'button',
    { class: 'btn btn-primary btn-sm', type: 'button' },
    ['Confirm transfer'],
  );
  const cancelTransferBtn = el(
    'button',
    { class: 'btn btn-ghost btn-sm', type: 'button' },
    ['Cancel'],
  );
  const transferForm = el('div', { class: 'button-row', hidden: true }, [
    toUserIdInput,
    reasonInput,
    confirmTransferBtn,
    cancelTransferBtn,
  ]);

  transferBtn.addEventListener('click', () => {
    transferForm.hidden = !transferForm.hidden;
  });
  cancelTransferBtn.addEventListener('click', () => {
    transferForm.hidden = true;
    toUserIdInput.value = '';
    reasonInput.value = '';
  });
  confirmTransferBtn.addEventListener('click', async () => {
    const toUserId = toUserIdInput.value.trim();
    if (!toUserId) {
      setMessage('Enter the target user ID.', 'error');
      return;
    }
    const reason = reasonInput.value.trim();
    if (!window.confirm(`Transfer this subscription to user ${toUserId}?`)) return;
    clearMessage();
    confirmTransferBtn.disabled = true;
    try {
      const result = await transferSubscription(detail.id, toUserId, reason || undefined);
      setMessage(result.message, result.success ? 'ok' : 'error');
      if (result.success) {
        transferForm.hidden = true;
        onChanged({ kind: 'transferred', linkId: detail.id, notice: result.message });
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Transfer failed.', 'error');
    } finally {
      confirmTransferBtn.disabled = false;
    }
  });

  const actions = el('div', { class: 'button-row' }, [
    verifyBtn,
    transferBtn,
    unlinkBtn,
    closeBtn,
  ]);

  container.append(
    head,
    meta,
    statusHost,
    actions,
    transferForm,
    section('Transfer history', transferHistoryTable(detail.transferHistory)),
  );
}

// ── Transfer log table (shared by detail history + the global audit log) ─

function transferHistoryTable(rows: TransferLogRow[]): HTMLElement {
  if (rows.length === 0) {
    return el('div', { class: 'empty' }, ['None.']);
  }
  const headers = ['When', 'From', 'To', 'Notes'];
  const thead = el('thead', {}, [
    el('tr', {}, headers.map((h) => el('th', { textContent: h }))),
  ]);
  const rowEls = rows.map((r) => {
    const isUnlink = r.toUserId.startsWith('ADMIN_UNLINK:');
    return el('tr', {}, [
      el('td', { class: 'cell-muted', textContent: fmtDate(r.transferredAt) }),
      el('td', { textContent: r.fromUserEmail || r.fromUserId }),
      el('td', {}, [
        isUnlink
          ? el('span', { class: 'pill pill-danger', textContent: 'Unlinked' })
          : el('span', { textContent: r.toUserEmail || r.toUserId }),
      ]),
      el('td', { class: 'cell-muted', textContent: r.userAgent ?? '—' }),
    ]);
  });
  return el('div', { class: 'table-wrap' }, [
    el('table', { class: 'data-table' }, [thead, el('tbody', {}, rowEls)]),
  ]);
}

// ── Shared small helpers ─────────────────────────────────────────────

function metaItem(label: string, value: string): HTMLElement {
  return el('div', { class: 'meta-item' }, [
    el('div', { class: 'meta-label', textContent: label }),
    el('div', { class: 'meta-value', textContent: value }),
  ]);
}

function section(title: string, tableEl: HTMLElement): HTMLElement {
  return el('section', { class: 'detail-section' }, [
    el('h2', { textContent: title }),
    tableEl,
  ]);
}

function selectControl(options: [string, string][], selected: string): HTMLSelectElement {
  const sel = el('select', { class: 'control' });
  for (const [value, label] of options) {
    const opt = el('option', { value, textContent: label });
    if (value === selected) opt.selected = true;
    sel.append(opt);
  }
  return sel;
}

function paginationRow(
  meta: PageMeta,
  noun: string,
  onPage: (page: number) => void,
): HTMLElement {
  const prev = el(
    'button',
    { class: 'btn btn-ghost', disabled: meta.page <= 1, onClick: () => onPage(meta.page - 1) },
    ['‹ Prev'],
  );
  const next = el(
    'button',
    {
      class: 'btn btn-ghost',
      disabled: !meta.hasNextPage,
      onClick: () => onPage(meta.page + 1),
    },
    ['Next ›'],
  );
  return el('div', { class: 'pagination' }, [
    prev,
    el('span', {
      class: 'cell-muted',
      textContent: `Page ${meta.page} of ${Math.max(meta.totalPages, 1)} · ${fmtInt(meta.totalItems)} ${noun}`,
    }),
    next,
  ]);
}

function isStale(lastVerifiedAt: string | null): boolean {
  if (!lastVerifiedAt) return true;
  const hours = (Date.now() - new Date(lastVerifiedAt).getTime()) / 3_600_000;
  return hours > 24;
}

function formatProductId(productId: string): string {
  const parts = productId.split('.');
  const tail = parts.slice(-2).join(' ');
  return tail.replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStatusLabel(status: string | null): string {
  if (!status) return '—';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusPillClass(status: string | null): string {
  switch (status) {
    case 'active':
      return 'pill pill-ok';
    case 'in_trial':
    case 'grace_period':
      return 'pill pill-warn';
    case 'expired':
    case 'revoked':
      return 'pill pill-danger';
    default:
      return 'pill';
  }
}
